import React, { useRef, useEffect, useState } from 'react';
import { useSceneScrollTimings, useScrollProgress } from '../../hooks/useSceneScrollTimings';
import { MoneyCounter } from '../shared/MoneyCounter';
import { Button } from '../ui/button';
import { gsap } from 'gsap';
import { Receipt } from 'lucide-react';

/**
 * Scene 1 - The Leak
 * 
 * Visualizes financial losses through floating receipt animations.
 * Scroll-driven counter shows increasing leak amount.
 * Loss aversion psychology: show pain before presenting solutions.
 * 
 * Copy: "Every year without strategy, money slips away."
 * Motion: Receipts drift downward, counter increases with scroll
 * Gradient: Navy → Deep red (urgency)
 */
export const Scene1Leak = ({ onSceneChange, onProgressUpdate }) => {
  const sectionRef = useRef(null);
  const receiptsRef = useRef(null);
  const [counterValue, setCounterValue] = useState(5000);

  // Receipt data with labels
  const receiptItems = [
    { id: 0, label: 'Missed deductions', amount: '$8,200', x: '10%', delay: 0 },
    { id: 1, label: 'Wrong entity choice', amount: '$12,500', x: '25%', delay: 0.1 },
    { id: 2, label: 'No depreciation', amount: '$6,800', x: '45%', delay: 0.2 },
    { id: 3, label: 'Unused retirement', amount: '$15,000', x: '65%', delay: 0.3 },
    { id: 4, label: 'Overlooked credits', amount: '$4,200', x: '80%', delay: 0.4 },
    { id: 5, label: 'Missed write-offs', amount: '$3,300', x: '15%', delay: 0.5 },
  ];

  // Track scroll progress to update counter
  useScrollProgress(sectionRef, (progress) => {
    // Counter increases from $5K to $50K based on scroll progress
    const newValue = 5000 + Math.floor(progress * 45000);
    setCounterValue(newValue);
    
    if (onProgressUpdate) {
      onProgressUpdate(5 + progress * 20); // 5-25% of total journey
    }
  });

  // Scene change tracking
  useSceneScrollTimings(sectionRef, {
    start: 'top center',
    end: 'bottom center',
    onEnter: () => {
      if (onSceneChange) onSceneChange(1);
    },
  });

  // Receipt animations
  useEffect(() => {
    if (!receiptsRef.current) return;

    const ctx = gsap.context(() => {
      const receipts = gsap.utils.toArray('.receipt-float');

      receipts.forEach((receipt, index) => {
        // Each receipt floats down with slight rotation
        gsap.fromTo(
          receipt,
          {
            y: -100,
            opacity: 0,
            rotation: gsap.utils.random(-8, 8),
          },
          {
            y: '120vh',
            opacity: 1,
            rotation: gsap.utils.random(-15, 15),
            duration: gsap.utils.random(12, 18),
            delay: receiptItems[index].delay,
            repeat: -1,
            ease: 'none',
          }
        );

        // Fade in at start, fade out at end
        gsap.to(receipt, {
          opacity: 0.8,
          yoyo: true,
          duration: 2,
          delay: receiptItems[index].delay,
        });
      });
    }, receiptsRef);

    return () => ctx.revert();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="relative min-h-screen flex items-center justify-center bg-gradient-leak overflow-hidden pt-20"
      data-testid="leak-section"
    >
      {/* Floating receipts background layer - HIDE ON MOBILE for better performance */}
      <div
        ref={receiptsRef}
        className="absolute inset-0 pointer-events-none hidden sm:block"
        data-testid="receipts-container"
      >
        {receiptItems.map((item) => (
          <div
            key={item.id}
            className="receipt-float absolute"
            style={{
              left: item.x,
              top: '-100px',
            }}
            data-testid={`receipt-float-${item.id}`}
          >
            {/* Receipt card with HIGH CONTRAST */}
            <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-lg p-4 shadow-lg min-w-[180px]">
              <Receipt className="w-8 h-8 text-white/70 mb-2" />
              <p className="text-white font-sans text-sm font-medium mb-1">
                {item.label}
              </p>
              <p className="text-teal-300 font-mono text-lg font-semibold">
                {item.amount}
              </p>
            </div>
          </div>
        ))}
      </div>

      {/* Content - HIGH CONTRAST with guaranteed visibility */}
      <div className="max-w-5xl mx-auto w-full px-6 sm:px-8 lg:px-12 py-20 relative z-10">
        <div className="text-center space-y-8">
          {/* Title - WHITE on dark red gradient */}
          <h2
            className="text-3xl sm:text-4xl lg:text-6xl font-display font-semibold text-white leading-tight max-w-4xl mx-auto"
            style={{ textShadow: '0 2px 20px rgba(0,0,0,0.6)' }}
            data-testid="leak-title"
          >
            Every year without strategy,
            <br />
            money slips away.
          </h2>

          {/* List of leaks - HIGH CONTRAST */}
          <div className="mt-12 space-y-3 max-w-2xl mx-auto">
            {[
              'Missed deductions',
              'Wrong entity choice',
              'No depreciation plan',
              'Unused retirement shields',
            ].map((item, index) => (
              <p
                key={index}
                className="text-lg sm:text-xl text-white/90 font-sans"
                style={{ textShadow: '0 1px 8px rgba(0,0,0,0.5)' }}
                data-testid={`leak-line-${index + 1}`}
              >
                • {item}
              </p>
            ))}
          </div>

          {/* Dynamic counter showing increasing leak */}
          <div className="mt-16 space-y-4">
            <p
              className="text-sm sm:text-base text-white/70 font-sans uppercase tracking-wider"
              data-testid="leak-counter-label"
            >
              Your annual leak estimate
            </p>
            <MoneyCounter
              end={counterValue}
              start={counterValue}
              duration={0.5}
              className="text-5xl sm:text-6xl lg:text-7xl text-red-300 drop-shadow-[0_0_24px_rgba(239,68,68,0.5)]"
              testId="leak-counter"
            />
          </div>

          {/* Tagline - HIGH CONTRAST: Light text */}
          <p
            className="text-lg sm:text-xl text-white/80 font-display italic max-w-3xl mx-auto mt-12"
            style={{ textShadow: '0 1px 12px rgba(0,0,0,0.5)' }}
            data-testid="leak-tagline"
          >
            Each receipt that drifts away was money you could've kept.
          </p>

          {/* CTA Button - MAXIMUM CONTRAST: Bright teal button */}
          <div className="mt-16">
            <Button
              size="lg"
              onClick={() => {
                console.log('Find My Leaks CTA clicked');
                // TODO: Open leak assessment modal or navigate to /assessment
                window.alert('🔍 Starting leak assessment...\n\nThis will help identify where your money is going.');
              }}
              className="h-14 px-8 text-base font-semibold bg-teal-400 hover:bg-teal-500 text-slate-900 shadow-xl hover:shadow-glow-teal transition-all duration-300"
              data-testid="find-leaks-cta-button"
            >
              Find My Leaks →
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};